/*
 * Copyright (c) 2026 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useSetAtom, useStore } from 'jotai'
import React, { useEffect } from 'react'

import type { PublicSession } from '@/app/api/session/route'
import { companyAtom, emailAtom, firstNameAtom, lastNameAtom, userIdAtom } from '@/store/session'

/**
 * Resolves the signed-in user on prerendered pages, which cannot carry identity in their
 * shared HTML.
 *
 * Sets the profile atoms only, never accessTokenAtom — the endpoint returns no token, and
 * the trees that need one hydrate it server-side.
 */
const PublicSessionHydration: React.FC = () => {
  const store = useStore()
  const setUserId = useSetAtom(userIdAtom)
  const setEmail = useSetAtom(emailAtom)
  const setFirstName = useSetAtom(firstNameAtom)
  const setLastName = useSetAtom(lastNameAtom)
  const setCompany = useSetAtom(companyAtom)

  useEffect(() => {
    // Skip where a layout already hydrated server-side. Read from the store, not during
    // render: SessionHydration is in a child layout, so at render time this is always empty.
    if (store.get(userIdAtom)) return

    const controller = new AbortController()

    const resolveSession = async () => {
      try {
        // Trailing slash avoids a 308 on every page load under `trailingSlash: true`.
        const response = await fetch('/api/session/', {
          signal: controller.signal,
          cache: 'no-store',
          credentials: 'same-origin',
        })
        // A 503 means the backend is unreachable, not that the visitor is anonymous.
        if (!response.ok) return

        const session = (await response.json()) as PublicSession
        if (!session.authenticated || !session.user) return

        setUserId(session.user.userId)
        setEmail(session.user.email)
        setFirstName(session.user.firstName)
        setLastName(session.user.lastName)
        setCompany(session.user.company)
      } catch {
        // Aborted or offline — the anonymous rendering already on screen stands.
      }
    }

    void resolveSession()
    return () => controller.abort()
  }, [store, setUserId, setEmail, setFirstName, setLastName, setCompany])

  return null
}

export { PublicSessionHydration }
